import { store } from "../store"
import { setAccessToken } from "../store/authSlice"
import { jwtDecode } from "jwt-decode"
import dayjs from "dayjs"

let authHandlers = {
  loginWithRedirect: null,
  logout: null,
  getAccessTokenSilently: null,
  getIdTokenClaims: null,
}

const ACCESS_TOKEN_STORAGE_KEY = "dng.auth.access_token"
let tokenGeneration = 0
let tokenRefresh: Promise<string | null> | null = null

// Public marketing site — where users land after logout or a failed login.
export const PUBLIC_APP_URL =
  process.env.REACT_APP_PUBLIC_APP_URL ||
  (typeof window !== "undefined" ? window.location.origin : "")

export const redirectToPublicApp = () => {
  if (typeof window !== "undefined" && PUBLIC_APP_URL) {
    window.location.replace(PUBLIC_APP_URL)
  }
}

const readTokenFromStorage = () => {
  if (typeof window === "undefined") return null
  try {
    const token = window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)
    return token || null
  } catch (_) {
    return null
  }
}

const writeTokenToStorage = (token) => {
  if (typeof window === "undefined") return
  try {
    if (token) window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, String(token))
    else window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
  } catch (_) {}
}

export const persistAccessToken = (token) => {
  const nextToken = token ? String(token) : null
  writeTokenToStorage(nextToken)
  store.dispatch(setAccessToken(nextToken))
  return nextToken
}

export const clearPersistedAccessToken = () => {
  tokenGeneration += 1
  tokenRefresh = null
  persistAccessToken(null)
}

export const getPersistedAccessToken = () => {
  const stateToken = store.getState()?.auth?.accessToken
  if (stateToken) return stateToken
  const storageToken = readTokenFromStorage()
  if (storageToken) {
    store.dispatch(setAccessToken(storageToken))
    return storageToken
  }
  return null
}

export const configureAuthClient = (handlers) => {
  authHandlers = {
    ...authHandlers,
    ...handlers,
  }
}

export const authLogin = async () => {
  if (typeof authHandlers.loginWithRedirect === "function") {
    return authHandlers.loginWithRedirect()
  }
}

export const authLogout = async () => {
  if (typeof authHandlers.logout === "function") {
    clearPersistedAccessToken()
    return authHandlers.logout({ logoutParams: { returnTo: PUBLIC_APP_URL } })
  }
}

export const getAccessTokenFromAuth = async () => {
  const ownerId = store.getState().auth.user?.sub
  if (!ownerId) return null
  const cachedToken = getPersistedAccessToken()
  if (cachedToken) {
    try {
      const claims = jwtDecode<{ sub?: string; exp?: number }>(cachedToken)
      if (claims.sub === ownerId && typeof claims.exp === "number" && claims.exp > dayjs().unix() + 60) return cachedToken
    } catch (_) {}
  }
  if (typeof authHandlers.getAccessTokenSilently !== "function") return null
  if (tokenRefresh) return tokenRefresh
  const generation = tokenGeneration
  const refresh = (async () => {
    // Reuse the SDK's valid login token. Forcing an iframe refresh here can
    // immediately lose the session in browsers that block third-party cookies.
    const fetchedToken = await authHandlers.getAccessTokenSilently({ cacheMode: "on" })
    if (generation !== tokenGeneration || store.getState().auth.user?.sub !== ownerId || !fetchedToken) return null
    const claims = jwtDecode<{ sub?: string }>(fetchedToken)
    if (claims.sub !== ownerId) return null
    return persistAccessToken(fetchedToken)
  })()
  tokenRefresh = refresh
  try { return await refresh } finally { if (tokenRefresh === refresh) tokenRefresh = null }
}

export const getIdTokenClaimsFromAuth = async () => {
  if (typeof authHandlers.getIdTokenClaims === "function") {
    return authHandlers.getIdTokenClaims()
  }
  return null
}
